import { ApiResponse } from 'apisauce';
import { useMemo } from 'react';
import config from '../../config';
import { DropboxFolder } from './DropboxTypes';
import { dropboxRepository } from './dropbox.repository';
import useDropboxFolders from './useDropboxFolders';

const DROPBOX_SETTINGS_PAGE = 'settings/dropbox';

const dropBoxParams = {
  token_access_type: 'offline',
  response_type: 'code',
  client_id: config.dropBoxClientId,
};

type DropboxConfigHook = {
  initDropboxConfig: () => void;
  validateCode: (code: string) => Promise<ApiResponse<any>>;
  setSelectedFolder: (folder: string) => Promise<ApiResponse<any>>;
  synchronizeFolders: () => Promise<void>;
  removeConfig: () => Promise<ApiResponse<any>>;
  folders: DropboxFolder[];
  isLoadingFolders: boolean;
};

const useDropboxConfig = (): DropboxConfigHook => {
  const dropboxApi = dropboxRepository();
  const { folders, isLoadingFolders, synchronizeFolders } = useDropboxFolders();

  const dropboxAuthUrlWithRedirect = useMemo(() => {
    const redirectUri = `${window.location.origin}/${DROPBOX_SETTINGS_PAGE}`;
    const dropboxAuthUrl = new URL(`${config.dropBoxOAuthUrl}/authorize`);
    dropboxAuthUrl.search = new URLSearchParams({
      ...dropBoxParams,
      redirect_uri: redirectUri,
    }).toString();
    return dropboxAuthUrl.toString();
  }, []);

  const initDropboxConfig = () => {
    window.location.href = dropboxAuthUrlWithRedirect;
  };

  const validateCode = async (code: string) => dropboxApi.validateCode(code);

  const removeConfig = () => dropboxApi.removeConfig();

  const setSelectedFolder = async (folder: string) => dropboxApi.updateFolder(folder);

  return {
    initDropboxConfig,
    validateCode,
    removeConfig,
    synchronizeFolders,
    setSelectedFolder,
    folders,
    isLoadingFolders,
  };
};

export default useDropboxConfig;
